import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CheckIcon, ClockIcon, ClipboardDocumentIcon, InformationCircleIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Button } from '../components/button';
import { Field, Label } from '../components/fieldset';
import { Input } from '../components/input';
import { Listbox, ListboxOption, ListboxLabel } from '../components/listbox';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../components/dialog';
import { Table, TableHeader, TableHead, TableRow, TableCell, TableBody } from '../components/table';
import { doc, updateDoc, setDoc, onSnapshot, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import InviteClientUserDialog from '../components/InviteClientUserDialog';
import { useClients, useConvenios } from '../contexts/DataContext';
import { logBitacora } from '../utils/bitacora';
import { Switch } from '../components/switch';
import { PORTAL_CONFIG_DEFAULTS } from '../hooks/usePortalConfig';

// Org-wide switches for the client portal (orgs/{orgId}/portal/config).
// Adding a future switch = one entry here + its default in PORTAL_CONFIG_DEFAULTS.
const PORTAL_SWITCHES = [
  {
    key: 'hasTarifa',
    label: 'Mostrar tarifa en el portal de clientes',
    description: 'Las fianzas en el portal muestran su tarifa.',
  },
  {
    key: 'showSaldos',
    label: 'Mostrar saldos en el portal de clientes',
    description: 'El cliente ve sus facturas por pagar y sus créditos a favor con cada afianzadora.',
  },
];

const ConfiguracionOrganizacion = ({
  user,
  userData,
  orgData,
  isOwner,
  hasDependientes,
  usersCache,
  fetchUserInfo,
  getUserEmail,
  getUserName,
  plan,
  planLoading,
}) => {
  // New user creation states
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    nombre: '',
    correo: '',
    rol: 'operator'
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createStatus, setCreateStatus] = useState('');
  const [createResult, setCreateResult] = useState(null);
  const [copyTooltip, setCopyTooltip] = useState('');
  const [updatingRoleFor, setUpdatingRoleFor] = useState(null);

  // Org name editing states
  const [isEditingOrgName, setIsEditingOrgName] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [isSavingOrgName, setIsSavingOrgName] = useState(false);

  // Roles guide dialog
  const [isRolesDialogOpen, setIsRolesDialogOpen] = useState(false);

  // Client users management
  const [isInviteClientUserOpen, setIsInviteClientUserOpen] = useState(false);
  const [clientUsers, setClientUsers] = useState([]);
  const [clientUsersLoading, setClientUsersLoading] = useState(false);
  const [editingClientUser, setEditingClientUser] = useState(null);
  const [editClientIds, setEditClientIds] = useState([]);
  const [editConvenioAccess, setEditConvenioAccess] = useState({});
  const [editSearchTerm, setEditSearchTerm] = useState('');
  const [editAccessTab, setEditAccessTab] = useState('clientes');
  const [savingClientUser, setSavingClientUser] = useState(false);
  const [deactivatingClientUser, setDeactivatingClientUser] = useState(null);
  const { clientsList } = useClients();
  const { conveniosById } = useConvenios();

  // Portal switches (org-wide client portal display preferences)
  const [portalConfig, setPortalConfig] = useState(null);
  const [portalConfigLoaded, setPortalConfigLoaded] = useState(false);
  const [savingPortalKey, setSavingPortalKey] = useState(null);

  // Org stats. The 100-member cap is a soft abuse guard, NOT a plan limit
  // — keep in lockstep with MAX_TEAM_MEMBERS in functions/index.js.
  const MAX_TEAM_MEMBERS = 100;
  const memberCount = orgData?.usuarios?.length || 0;
  const ownerCount = (orgData?.usuarios || []).filter(
    (uid) => usersCache[uid]?.rol === "owner"
  ).length;
  const operatorCount = memberCount - ownerCount;
  const atMemberCap = memberCount >= MAX_TEAM_MEMBERS;

  // Live portal config for this org
  useEffect(() => {
    if (!userData?.org || !isOwner) return;
    const unsubscribe = onSnapshot(
      doc(db, 'orgs', userData.org, 'portal', 'config'),
      (snap) => {
        setPortalConfig(snap.exists() ? snap.data() : null);
        setPortalConfigLoaded(true);
      },
      (err) => {
        console.error('Error fetching portal config:', err);
        setPortalConfigLoaded(true);
      }
    );
    return () => unsubscribe();
  }, [userData?.org, isOwner]);

  const handleTogglePortalSwitch = async (key, value) => {
    if (!userData?.org || !isOwner || savingPortalKey) return;
    setSavingPortalKey(key);
    try {
      await setDoc(
        doc(db, 'orgs', userData.org, 'portal', 'config'),
        { [key]: value },
        { merge: true }
      );
      try {
        const sw = PORTAL_SWITCHES.find((s) => s.key === key);
        await logBitacora({
          action: 'UPDATE',
          collection: 'orgs',
          documentId: userData.org,
          metadata: {
            description: `Configuración del portal: "${sw?.label || key}" ${value ? 'activado' : 'desactivado'}`,
          },
          user,
          userData,
        });
      } catch (logErr) {
        console.error('Failed to log portal config update:', logErr);
      }
    } catch (err) {
      console.error('Error updating portal config:', err);
    } finally {
      setSavingPortalKey(null);
    }
  };

  // Fetch client users for this org
  useEffect(() => {
    if (!userData?.org || !isOwner) return;
    setClientUsersLoading(true);
    const fetchClientUsers = async () => {
      try {
        const q = query(
          collection(db, 'usuarios'),
          where('org', '==', userData.org),
          where('rol', '==', 'client')
        );
        const snap = await getDocs(q);
        setClientUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error fetching client users:', err);
      } finally {
        setClientUsersLoading(false);
      }
    };
    fetchClientUsers();
  }, [userData?.org, isOwner, isInviteClientUserOpen]);

  const handleEditClientUser = (cu) => {
    setEditingClientUser(cu);
    setEditClientIds(cu.clientIds || []);
    setEditConvenioAccess(cu.convenioAccess || {});
    setEditSearchTerm('');
    setEditAccessTab('clientes');
  };

  const handleSaveClientUserAccess = async () => {
    if (!editingClientUser) return;
    setSavingClientUser(true);
    try {
      await updateDoc(doc(db, 'usuarios', editingClientUser.id), {
        clientIds: editClientIds,
        convenioAccess: editConvenioAccess,
      });

      try {
        const clientNames = editClientIds.map((id) => clientsList.find((c) => c.id === id)?.nombreEmpresa || id).join(', ');
        const convenioNames = Object.keys(editConvenioAccess).map((id) => conveniosById[id]?.nombre || id).join(', ');
        const parts = [];
        if (clientNames) parts.push(`Clientes: ${clientNames}`);
        if (convenioNames) parts.push(`Convenios: ${convenioNames}`);
        await logBitacora({
          action: 'UPDATE',
          collection: 'usuarios',
          documentId: editingClientUser.id,
          metadata: {
            description: `Actualizó acceso de "${editingClientUser.nombre || editingClientUser.correo}". ${parts.join('. ')}`,
          },
          user,
          userData,
        });
      } catch (logErr) {
        console.error('Failed to log client user access update:', logErr);
      }

      // Update local state
      setClientUsers((prev) =>
        prev.map((cu) =>
          cu.id === editingClientUser.id
            ? { ...cu, clientIds: editClientIds, convenioAccess: editConvenioAccess }
            : cu
        )
      );
      setEditingClientUser(null);
    } catch (err) {
      console.error('Error updating client user access:', err);
    } finally {
      setSavingClientUser(false);
    }
  };

  const handleDeactivateClientUser = async (cu) => {
    if (!window.confirm(`¿Desactivar el acceso de ${cu.nombre || cu.correo}? Ya no podrá iniciar sesión.`)) return;
    setDeactivatingClientUser(cu.id);
    try {
      const functions = getFunctions();
      const disableUser = httpsCallable(functions, 'disableClientUser');
      await disableUser({ userId: cu.id });
      setClientUsers((prev) => prev.filter((c) => c.id !== cu.id));

      try {
        await logBitacora({
          action: 'DELETE',
          collection: 'usuarios',
          documentId: cu.id,
          metadata: {
            description: `Desactivó cuenta de cliente "${cu.nombre || cu.correo}"`,
          },
          user,
          userData,
        });
      } catch (logErr) {
        console.error('Failed to log client user deactivation:', logErr);
      }
    } catch (err) {
      // Fallback: just clear clientIds so they see nothing
      console.error('Error deactivating client user (falling back to clearing clientIds):', err);
      try {
        await updateDoc(doc(db, 'usuarios', cu.id), { clientIds: [] });
        setClientUsers((prev) =>
          prev.map((c) => (c.id === cu.id ? { ...c, clientIds: [] } : c))
        );
      } catch (updateErr) {
        console.error('Error clearing clientIds:', updateErr);
      }
    } finally {
      setDeactivatingClientUser(null);
    }
  };

  const toggleEditClientId = (clientId) => {
    setEditClientIds((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const toggleEditConvenio = (convenioId) => {
    setEditConvenioAccess((prev) => {
      if (convenioId in prev) {
        const next = { ...prev };
        delete next[convenioId];
        return next;
      }
      return { ...prev, [convenioId]: { fullAccess: true, proyectoIds: [] } };
    });
  };

  const setConvenioFullAccess = (convenioId, fullAccess) => {
    setEditConvenioAccess((prev) => ({
      ...prev,
      [convenioId]: { ...prev[convenioId], fullAccess, proyectoIds: fullAccess ? [] : (prev[convenioId]?.proyectoIds || []) },
    }));
  };

  const toggleConvenioProject = (convenioId, proyectoId) => {
    setEditConvenioAccess((prev) => {
      const entry = prev[convenioId] || { fullAccess: false, proyectoIds: [] };
      const proyectoIds = entry.proyectoIds || [];
      const newProyectoIds = proyectoIds.includes(proyectoId)
        ? proyectoIds.filter((id) => id !== proyectoId)
        : [...proyectoIds, proyectoId];
      return { ...prev, [convenioId]: { ...entry, fullAccess: false, proyectoIds: newProyectoIds } };
    });
  };

  const handleNewUserInputChange = (e) => {
    const { name, value } = e.target;
    setNewUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const createNewUser = async () => {
    if (!user || !userData) return;

    if (!newUserData.nombre.trim() || !newUserData.correo.trim()) {
      setCreateStatus('Por favor completa todos los campos requeridos');
      return;
    }

    setIsCreating(true);
    setCreateStatus('Creando usuario...');

    try {
      const functions = getFunctions();
      const createTeamMember = httpsCallable(functions, 'createTeamMember');

      const createParams = {
        nombre: newUserData.nombre.trim(),
        correo: newUserData.correo.trim()
      };

      if (userData?.org) {
        createParams.org = userData.org;
        createParams.rol = newUserData.rol;
      }

      const result = await createTeamMember(createParams);

      setCreateResult(result.data);
      setCreateStatus('Usuario creado exitosamente.');
      setNewUserData({
        nombre: '',
        correo: '',
        rol: 'operator'
      });
      setIsCreatingUser(false);

    } catch (error) {
      console.error('Error creating user:', error);
      // Cloud Function throws HttpsError with a Spanish message; use it
      // directly. Fall back to a generic message only when we got something
      // truly opaque (network error, unhandled internal, etc.).
      const isOpaque = !error.message ||
        error.message === 'INTERNAL' ||
        error.code === 'functions/internal';
      const errorMessage = isOpaque
        ? 'Error al crear el usuario. Intenta de nuevo o contacta a soporte.'
        : error.message;
      setCreateStatus(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setIsCreatingUser(false);
    setNewUserData({
      nombre: '',
      correo: '',
      rol: 'operator'
    });
    setCreateStatus('');
    setCreateResult(null);
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    if (!userData?.org || !isOwner) return;

    if (userId === user?.uid) {
      console.warn("Cannot change your own role");
      return;
    }

    setUpdatingRoleFor(userId);
    try {
      await updateDoc(doc(db, "usuarios", userId), { rol: newRole });
      await fetchUserInfo(userId);
    } catch (error) {
      console.error("Error updating user role:", error);
    } finally {
      setUpdatingRoleFor(null);
    }
  };

  const getUserRole = (userId) => {
    return usersCache[userId]?.rol || 'operator';
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyTooltip('¡Copiado!');
      setTimeout(() => setCopyTooltip(''), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      setCopyTooltip('Error al copiar');
      setTimeout(() => setCopyTooltip(''), 2000);
    }
  };

  const handleStartEditOrgName = () => {
    setNewOrgName(orgData?.nombre || "");
    setIsEditingOrgName(true);
  };

  const handleCancelEditOrgName = () => {
    setIsEditingOrgName(false);
    setNewOrgName("");
  };

  const handleSaveOrgName = async () => {
    if (!newOrgName.trim() || !orgData?.id) return;

    setIsSavingOrgName(true);
    try {
      await updateDoc(doc(db, "orgs", orgData.id), {
        nombre: newOrgName.trim(),
      });
      setIsEditingOrgName(false);
    } catch (error) {
      console.error("Error updating org name:", error);
    } finally {
      setIsSavingOrgName(false);
    }
  };

  // Get team members list
  const teamMembers = userData?.org
    ? (orgData?.usuarios || []).filter(uid => uid !== user?.uid)
    : (userData?.dependientes || []);

  return (
    <>
      {(userData?.org || hasDependientes) ? (
        <div className="space-y-6">
          {/* Organization Info - Only for org-based users */}
          {userData?.org && orgData && (
            <>
              {/* Organization Details */}
              <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6 space-y-4">
                {/* Organization Name as Header */}
                {isEditingOrgName ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={newOrgName}
                      onChange={(e) => setNewOrgName(e.target.value)}
                      placeholder="Nombre de la organización"
                      className="flex-1 max-w-md text-lg"
                      autoFocus
                    />
                    <button
                      onClick={handleSaveOrgName}
                      disabled={isSavingOrgName || !newOrgName.trim()}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <CheckIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleCancelEditOrgName}
                      disabled={isSavingOrgName}
                      className="p-2 text-gray-500 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Heading level={2}>
                      {orgData.nombre || "Mi Organización"}
                    </Heading>
                    {isOwner && (
                      <button
                        onClick={handleStartEditOrgName}
                        className="p-1.5 text-azul-surelia/50 hover:text-azul-surelia hover:bg-azul-surelia/5 rounded-lg transition-colors"
                        title="Editar nombre"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}

                {/* Organization ID */}
                <div>
                  <Text className="text-sm font-medium text-azul-surelia/70 mb-2">
                    ID de la Organización
                  </Text>
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono bg-gray-100 px-3 py-1.5 rounded-lg text-gray-700">
                      {orgData.id}
                    </code>
                    <div className="relative">
                      <button
                        onClick={() => copyToClipboard(orgData.id)}
                        className="p-1.5 text-azul-surelia/50 hover:text-azul-surelia hover:bg-azul-surelia/5 rounded-lg transition-colors"
                        title="Copiar ID"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                      {copyTooltip && (
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          {copyTooltip}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-4 text-center">
                  <div className="text-3xl font-bold text-azul-surelia">{memberCount}</div>
                  <Text className="text-sm text-azul-surelia/70">Miembros</Text>
                </div>
                <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-4 text-center">
                  <div className="text-3xl font-bold text-azul-surelia">{ownerCount}</div>
                  <Text className="text-sm text-azul-surelia/70">Propietarios</Text>
                </div>
                <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-4 text-center">
                  <div className="text-3xl font-bold text-azul-surelia">{operatorCount}</div>
                  <Text className="text-sm text-azul-surelia/70">Operadores</Text>
                </div>
              </div>
            </>
          )}

          {/* Mi Equipo Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <Heading level={2}>Mi Equipo</Heading>
              {isOwner && !isCreatingUser && !createResult && (
                <Button
                  onClick={() => setIsCreatingUser(true)}
                  disabled={atMemberCap}
                  title={atMemberCap
                    ? `Has alcanzado el límite de ${MAX_TEAM_MEMBERS} miembros. Contáctanos para ampliarlo.`
                    : undefined}
                >
                  Crear Usuario
                </Button>
              )}
            </div>
            <Text className="text-gray-600 mb-2">
              {userData?.org
                ? `Miembros de ${orgData?.nombre || 'la organización'}`
                : 'Miembros de tu equipo'}
            </Text>
            {isOwner && atMemberCap && (
              <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <Text className="text-sm text-yellow-800">
                  <strong>Has alcanzado el límite de {MAX_TEAM_MEMBERS} miembros.</strong>{' '}
                  Contáctanos si necesitas ampliarlo.
                </Text>
              </div>
            )}
            {!(isOwner && atMemberCap) && <div className="mb-6" />}

            {/* Create User Form - Inline */}
            {isCreatingUser && (
              <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <Heading level={3}>Nuevo Usuario</Heading>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field>
                    <Label htmlFor="new-user-nombre">Nombre Completo *</Label>
                    <Input
                      id="new-user-nombre"
                      name="nombre"
                      value={newUserData.nombre}
                      onChange={handleNewUserInputChange}
                      placeholder="Nombre completo del usuario"
                      disabled={isCreating}
                    />
                  </Field>

                  <Field>
                    <Label htmlFor="new-user-correo">Correo Electrónico *</Label>
                    <Input
                      id="new-user-correo"
                      name="correo"
                      type="email"
                      value={newUserData.correo}
                      onChange={handleNewUserInputChange}
                      placeholder="usuario@ejemplo.com"
                      disabled={isCreating}
                    />
                  </Field>

                  {userData?.org && (
                    <Field>
                      <Label htmlFor="new-user-rol">Rol *</Label>
                      <Listbox
                        value={newUserData.rol}
                        onChange={(value) => setNewUserData(prev => ({ ...prev, rol: value }))}
                        disabled={isCreating}
                      >
                        <ListboxOption value="operator">
                          <ListboxLabel>Operador</ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="owner">
                          <ListboxLabel>Propietario</ListboxLabel>
                        </ListboxOption>
                      </Listbox>
                      <Text className="text-xs text-gray-500 mt-1">
                        {newUserData.rol === 'owner'
                          ? 'Los propietarios pueden ver todos los datos de la organización y crear usuarios.'
                          : 'Los operadores solo ven los clientes que crean o les son asignados.'}
                      </Text>
                    </Field>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <Text className="text-sm text-blue-800">
                    Le enviaremos un correo a este usuario con un enlace para establecer su contraseña.
                    Si no le llega, podrá restablecerla desde la pantalla de inicio de sesión.
                  </Text>
                </div>

                {createStatus && !createResult && (
                  <div className={`p-3 rounded-md ${
                    createStatus.includes('Error') || createStatus.includes('inválido') || createStatus.includes('requeridos')
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-green-50 text-green-700 border border-green-200'
                  }`}>
                    <Text className="text-sm">{createStatus}</Text>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={handleCancelCreate}
                    disabled={isCreating}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={createNewUser}
                    disabled={isCreating || !newUserData.nombre.trim() || !newUserData.correo.trim()}
                  >
                    {isCreating ? 'Creando...' : 'Crear Usuario'}
                  </Button>
                </div>
              </div>
            )}

            {/* Success Result */}
            {createResult && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <Heading level={3} className="text-green-800 mb-3 flex items-center">
                  <CheckIcon className="w-5 h-5 mr-2 text-green-600" />
                  {createResult.emailSent
                    ? 'Usuario creado y correo de bienvenida enviado'
                    : 'Usuario creado exitosamente'}
                </Heading>

                <div className="space-y-3">
                  <div>
                    <Text className="text-sm font-medium text-green-800">Nombre:</Text>
                    <Text className="text-green-700">{createResult.userName}</Text>
                  </div>

                  <div>
                    <Text className="text-sm font-medium text-green-800">Email:</Text>
                    <Text className="text-green-700">{createResult.userEmail}</Text>
                  </div>

                  {createResult.emailSent ? (
                    <div className="bg-azul-surelia/5 border border-azul-surelia/20 rounded p-3">
                      <Text className="text-sm text-azul-surelia flex items-start">
                        <CheckIcon className="w-4 h-4 mr-2 mt-0.5 text-azul-surelia flex-shrink-0" />
                        <span>Le enviamos un correo a <strong>{createResult.userEmail}</strong> con un enlace para establecer su contraseña. Si no llega, puede restablecerla desde la pantalla de inicio de sesión con ese mismo correo.</span>
                      </Text>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                      <Text className="text-sm text-yellow-800 flex items-start">
                        <ClockIcon className="w-4 h-4 mr-2 mt-0.5 text-yellow-600 flex-shrink-0" />
                        <span><strong>No pudimos enviar el correo automáticamente.</strong> Pide a {createResult.userName} que entre a la pantalla de inicio de sesión y use "¿Olvidaste tu contraseña?" con <strong>{createResult.userEmail}</strong>.</span>
                      </Text>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      onClick={() => {
                        setCreateResult(null);
                        setCreateStatus('');
                      }}
                    >
                      Crear Otro Usuario
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Nombre</TableHeader>
                  <TableHeader>Correo</TableHeader>
                  {userData?.org && (
                    <TableHeader>
                      <div className="flex items-center gap-1">
                        Rol
                        <button
                          onClick={() => setIsRolesDialogOpen(true)}
                          className="p-0.5 text-azul-surelia/50 hover:text-azul-surelia hover:bg-azul-surelia/5 rounded transition-colors"
                          title="Guía de Roles"
                        >
                          <InformationCircleIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </TableHeader>
                  )}
                  <TableHeader>Estado</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamMembers.map((memberId) => {
                  const memberRole = getUserRole(memberId);
                  const isUpdatingRole = updatingRoleFor === memberId;

                  return (
                    <TableRow key={memberId}>
                      <TableCell>
                        <Text className="font-medium">
                          {getUserName(memberId)}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text className="text-gray-600">
                          {getUserEmail(memberId)}
                        </Text>
                      </TableCell>
                      {userData?.org && (
                        <TableCell>
                          {isOwner ? (
                            <>
                              <Listbox
                                value={memberRole}
                                onChange={(newRole) => handleUpdateUserRole(memberId, newRole)}
                                disabled={isUpdatingRole}
                                className="min-w-[140px]"
                              >
                                <ListboxOption value="operator">
                                  <ListboxLabel>Operador</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value="owner">
                                  <ListboxLabel>Propietario</ListboxLabel>
                                </ListboxOption>
                              </Listbox>
                              {isUpdatingRole && (
                                <Text className="text-xs text-gray-500 mt-1">Actualizando...</Text>
                              )}
                            </>
                          ) : (
                            <Text className="text-gray-700">
                              {memberRole === 'owner' ? 'Propietario' : 'Operador'}
                            </Text>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Activo
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {teamMembers.length === 0 && (
              <div className="text-center py-8">
                <Text className="text-gray-500">
                  No hay miembros en tu equipo aún.
                </Text>
              </div>
            )}
          </div>

          {/* Client Users Section - Only for org owners */}
          {userData?.org && isOwner && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <Heading level={2}>Usuarios Cliente</Heading>
                <Button onClick={() => setIsInviteClientUserOpen(true)}>
                  Invitar Cliente
                </Button>
              </div>
              <Text className="text-gray-600 mb-6">
                Usuarios con acceso de solo lectura a los expedientes y fianzas de sus empresas.
              </Text>

              {clientUsersLoading ? (
                <div className="text-center py-4">
                  <Text className="text-gray-500">Cargando...</Text>
                </div>
              ) : clientUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Text className="text-gray-500">
                    No hay usuarios cliente aún. Invita a tus clientes para que puedan consultar sus expedientes.
                  </Text>
                </div>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Nombre</TableHeader>
                      <TableHeader>Correo</TableHeader>
                      <TableHeader>Clientes Asignados</TableHeader>
                      <TableHeader>Convenios</TableHeader>
                      <TableHeader>Acciones</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientUsers.map((cu) => {
                      const cuClientIds = cu.clientIds || [];
                      const clientNames = cuClientIds
                        .map((cid) => {
                          const found = (clientsList || []).find((c) => c.id === cid);
                          return found?.nombreEmpresa || cid;
                        });

                      return (
                        <TableRow key={cu.id}>
                          <TableCell>
                            <Text className="font-medium">{cu.nombre || '--'}</Text>
                          </TableCell>
                          <TableCell>
                            <Text className="text-gray-600">{cu.correo || '--'}</Text>
                          </TableCell>
                          <TableCell>
                            {clientNames.length === 0 ? (
                              <Text className="text-sm text-gray-400 italic">Sin acceso</Text>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {clientNames.map((name, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-azul-surelia/10 text-azul-surelia"
                                  >
                                    {name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const access = cu.convenioAccess || {};
                              const convenioIds = Object.keys(access);
                              if (convenioIds.length === 0) {
                                return <Text className="text-sm text-gray-400 italic">Sin acceso</Text>;
                              }
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {convenioIds.map((cid) => {
                                    const conv = conveniosById[cid];
                                    const name = conv?.nombre || cid;
                                    const isFullAccess = access[cid]?.fullAccess;
                                    return (
                                      <span
                                        key={cid}
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
                                        title={isFullAccess ? 'Acceso completo' : `Proyectos: ${(access[cid]?.proyectoIds || []).length}`}
                                      >
                                        {name}
                                        {!isFullAccess && (
                                          <span className="ml-1 text-purple-400">
                                            ({(access[cid]?.proyectoIds || []).length}p)
                                          </span>
                                        )}
                                      </span>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleEditClientUser(cu)}
                                className="text-sm text-azul-surelia hover:underline"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleDeactivateClientUser(cu)}
                                disabled={deactivatingClientUser === cu.id}
                                className="text-sm text-red-600 hover:underline disabled:opacity-50"
                              >
                                {deactivatingClientUser === cu.id ? '...' : 'Desactivar'}
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          )}

          {/* Portal Settings Section - Only for org owners */}
          {userData?.org && isOwner && (
            <div className="bg-white rounded-lg shadow p-6">
              <Heading level={2} className="mb-4">Portal de Clientes</Heading>
              <Text className="text-gray-600 mb-6">
                Controla qué información ven los usuarios cliente en el portal.
              </Text>
              {!portalConfigLoaded ? (
                <div className="text-center py-4">
                  <Text className="text-gray-500">Cargando...</Text>
                </div>
              ) : (
                <div className="max-w-2xl space-y-3">
                  {PORTAL_SWITCHES.map((sw) => {
                    const effective = { ...PORTAL_CONFIG_DEFAULTS, ...(portalConfig || {}) };
                    return (
                      <div
                        key={sw.key}
                        className="flex items-center justify-between gap-4 rounded-lg border border-azul-surelia/20 p-4"
                      >
                        <div>
                          <div className="font-medium">{sw.label}</div>
                          <Text className="mt-1 text-sm text-gray-600">{sw.description}</Text>
                        </div>
                        <Switch
                          color="emerald"
                          className="shrink-0"
                          checked={effective[sw.key]}
                          disabled={savingPortalKey === sw.key}
                          onChange={(value) => handleTogglePortalSwitch(sw.key, value)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <Heading level={2} className="mb-4">No tienes equipo asignado</Heading>
          <Text className="text-gray-600">
            {userData?.org
              ? 'Solo los propietarios de la organización pueden crear y gestionar usuarios.'
              : 'No tienes usuarios que reporten directamente a ti.'}
          </Text>
        </div>
      )}

      {/* Roles Guide Dialog */}
      <Dialog open={isRolesDialogOpen} onClose={() => setIsRolesDialogOpen(false)}>
        <DialogTitle>Guía de Roles</DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Text className="font-semibold text-blue-900 mb-2">Propietario (Owner)</Text>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Ve todos los clientes de la organización</li>
                <li>Puede crear y gestionar usuarios</li>
                <li>Acceso a Reportes</li>
                <li>Puede ver y editar tarifas de comisiones</li>
                <li>Puede editar información de la organización</li>
              </ul>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Text className="font-semibold text-gray-900 mb-2">Operador</Text>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Ve todos los clientes de la organización</li>
                <li>Puede crear clientes</li>
                <li>No puede crear usuarios</li>
                <li>Sin acceso a Reportes</li>
                <li>Sin acceso a tarifas de comisiones</li>
              </ul>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <Text className="font-semibold text-green-900 mb-2">Cliente</Text>
              <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                <li>Acceso de solo lectura</li>
                <li>Ve solo los clientes (empresas) que le son asignados</li>
                <li>Puede consultar expedientes, contratos y fianzas</li>
                <li>No puede crear, editar ni eliminar datos</li>
                <li>Portal simplificado independiente</li>
              </ul>
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button onClick={() => setIsRolesDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Client User Dialog */}
      <InviteClientUserDialog
        isOpen={isInviteClientUserOpen}
        onClose={() => setIsInviteClientUserOpen(false)}
        clientsList={clientsList || []}
        onCreated={async ({ nombre, correo, clientIds: cIds }) => {
          try {
            const clientNames = cIds.map((id) => clientsList.find((c) => c.id === id)?.nombreEmpresa || id).join(', ');
            await logBitacora({
              action: 'CREATE',
              collection: 'usuarios',
              documentId: correo,
              metadata: {
                description: `Creó cuenta de cliente "${nombre}" (${correo}) con acceso a: ${clientNames}`,
              },
              user,
              userData,
            });
          } catch (logErr) {
            console.error('Failed to log client user creation:', logErr);
          }
        }}
      />

      {/* Edit Client User Access Dialog */}
      <Dialog open={!!editingClientUser} onClose={() => !savingClientUser && setEditingClientUser(null)} size="lg">
        <DialogTitle>
          Editar acceso de {editingClientUser?.nombre || 'usuario'}
        </DialogTitle>
        <DialogBody>
          {/* Tab switcher */}
          <div className="flex gap-1 mb-4 border-b border-azul-surelia/20">
            {[
              { key: 'clientes', label: `Clientes (${editClientIds.length})` },
              { key: 'convenios', label: `Convenios (${Object.keys(editConvenioAccess).length})` },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => { setEditAccessTab(tab.key); setEditSearchTerm(''); }}
                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                  editAccessTab === tab.key
                    ? 'text-azul-surelia border-azul-surelia bg-azul-surelia/5'
                    : 'text-gray-500 border-transparent hover:text-azul-surelia hover:border-azul-surelia/40'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <Input
            placeholder={editAccessTab === 'clientes' ? 'Buscar cliente...' : 'Buscar convenio...'}
            value={editSearchTerm}
            onChange={(e) => setEditSearchTerm(e.target.value)}
            className="mb-3"
            disabled={savingClientUser}
          />

          {editAccessTab === 'clientes' ? (
            <>
              <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                {(clientsList || [])
                  .filter((c) => {
                    if (!editSearchTerm) return true;
                    const term = editSearchTerm.toLowerCase();
                    return (
                      (c.nombreEmpresa || '').toLowerCase().includes(term) ||
                      (c.rfc || '').toLowerCase().includes(term)
                    );
                  })
                  .sort((a, b) => {
                    const aSelected = editClientIds.includes(a.id) ? 0 : 1;
                    const bSelected = editClientIds.includes(b.id) ? 0 : 1;
                    if (aSelected !== bSelected) return aSelected - bSelected;
                    return (a.nombreEmpresa || '').localeCompare(b.nombreEmpresa || '');
                  })
                  .map((client) => (
                    <label
                      key={client.id}
                      className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                        editClientIds.includes(client.id) ? 'bg-azul-surelia/5' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={editClientIds.includes(client.id)}
                        onChange={() => toggleEditClientId(client.id)}
                        disabled={savingClientUser}
                        className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                      />
                      <div className="min-w-0 flex-1">
                        <Text className="text-sm font-medium truncate">
                          {client.nombreEmpresa || 'Sin nombre'}
                        </Text>
                        {client.rfc && (
                          <Text className="text-xs text-gray-400 font-mono">{client.rfc}</Text>
                        )}
                      </div>
                    </label>
                  ))}
              </div>
              <Text className="text-xs text-gray-500 mt-2">
                {editClientIds.length} cliente{editClientIds.length !== 1 ? 's' : ''} seleccionado{editClientIds.length !== 1 ? 's' : ''}
              </Text>
            </>
          ) : (
            <>
              <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                {Object.values(conveniosById)
                  .filter((c) => {
                    if (!editSearchTerm) return true;
                    return (c.nombre || '').toLowerCase().includes(editSearchTerm.toLowerCase());
                  })
                  .sort((a, b) => {
                    const aSelected = a.id in editConvenioAccess ? 0 : 1;
                    const bSelected = b.id in editConvenioAccess ? 0 : 1;
                    if (aSelected !== bSelected) return aSelected - bSelected;
                    return (a.nombre || '').localeCompare(b.nombre || '');
                  })
                  .map((conv) => {
                    const isSelected = conv.id in editConvenioAccess;
                    const access = editConvenioAccess[conv.id];
                    const proyectos = conv.proyectos || [];

                    return (
                      <div key={conv.id} className={`border-b border-gray-100 last:border-b-0 ${isSelected ? 'bg-purple-50/50' : ''}`}>
                        <label className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleEditConvenio(conv.id)}
                            disabled={savingClientUser}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div className="min-w-0 flex-1">
                            <Text className="text-sm font-medium truncate">{conv.nombre}</Text>
                          </div>
                        </label>

                        {/* Access level options when selected */}
                        {isSelected && (
                          <div className="pl-10 pr-3 pb-3 space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`access-${conv.id}`}
                                checked={access?.fullAccess === true}
                                onChange={() => setConvenioFullAccess(conv.id, true)}
                                disabled={savingClientUser}
                                className="text-purple-600 focus:ring-purple-500"
                              />
                              <Text className="text-xs">Acceso completo</Text>
                            </label>

                            {proyectos.length > 0 && (
                              <>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`access-${conv.id}`}
                                    checked={access?.fullAccess !== true}
                                    onChange={() => setConvenioFullAccess(conv.id, false)}
                                    disabled={savingClientUser}
                                    className="text-purple-600 focus:ring-purple-500"
                                  />
                                  <Text className="text-xs">Solo proyectos específicos</Text>
                                </label>

                                {access?.fullAccess !== true && (
                                  <div className="pl-5 space-y-1">
                                    {proyectos.map((p) => (
                                      <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={(access?.proyectoIds || []).includes(p.id)}
                                          onChange={() => toggleConvenioProject(conv.id, p.id)}
                                          disabled={savingClientUser}
                                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        />
                                        <Text className="text-xs">{p.nombre}</Text>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
              <Text className="text-xs text-gray-500 mt-2">
                {Object.keys(editConvenioAccess).length} convenio{Object.keys(editConvenioAccess).length !== 1 ? 's' : ''} seleccionado{Object.keys(editConvenioAccess).length !== 1 ? 's' : ''}
              </Text>
            </>
          )}
        </DialogBody>
        <DialogActions>
          <Button variant="outline" onClick={() => setEditingClientUser(null)} disabled={savingClientUser}>
            Cancelar
          </Button>
          <Button onClick={handleSaveClientUserAccess} disabled={savingClientUser}>
            {savingClientUser ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfiguracionOrganizacion;
